/**
 * DESIGN mode for the V5 Aircraft workbench - project-scoped editable aircraft.
 *
 * A design is a real .vsp3 file owned by the project (created blank, or copied
 * from a catalog model / another design). Every edit - add/rename/delete a
 * component, set a parm - is sent as an ops batch to
 * `/projects/{id}/aircraft-designs/{design_id}/ops`, applied worker-side
 * through OpenVSP, saved in place, and appended to the design node's ops_log.
 * That log IS the provenance record - the toolbar's "N edits recorded" hint is
 * its length.
 *
 * The returned bundle renders in the same viewer as Browse-mode builds, and
 * the refreshed parm tree drives the same AircraftParamPanel - here each
 * debounced (500ms) slider burst becomes a persisted {"op":"set_parms"}
 * instead of a transient build override. Ops requests are serialized on a
 * promise chain (every request rewrites the same .vsp3) with a seq guard so a
 * stale response never lands after a design switch (mirrors AircraftSurface's
 * buildSeq pattern). "Design control surfaces" exports the design to STEP
 * first, then seeds the CS workbench with the real exported file.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";

import {
  applyAircraftDesignOps, createAircraftDesign, deleteAircraftDesign,
  exportAircraftDesignStep, listAircraftDesigns,
  type AircraftBuildResponse, type AircraftCatalogModel, type AircraftDesignGeom, type AircraftDesignGeomType,
  type AircraftDesignOp, type AircraftDesignOpsResponse, type AircraftDesignSummary,
  type AircraftGeomParms,
} from "../../api/aircraft";
import { AircraftComponentTree } from "./AircraftComponentTree";
import { AircraftParamPanel } from "./AircraftParamPanel";
import { AircraftViewer3D } from "./AircraftViewer3D";
import { publishSourceSeed } from "./csSourceSeed";
import type { SurfaceProps } from "./ThreadV5Surfaces";

const RESOLUTIONS = [8, 12, 16, 24];

export type AircraftMode = "browse" | "design";

const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const isGone = (msg: string) => msg.startsWith("API 404");

/** The Browse | Design toolbar toggle - rendered by both modes so it never moves. */
export function AircraftModeToggle({ mode, onChange }: {
  mode: AircraftMode;
  onChange: (mode: AircraftMode) => void;
}) {
  return (
    <div style={S.modeWrap} role="tablist" aria-label="Aircraft mode">
      {(["browse", "design"] as const).map((m) => (
        <button key={m} role="tab" aria-selected={mode === m}
          style={{ ...S.modeTab, ...(mode === m ? S.modeTabOn : null) }} onClick={() => onChange(m)}>
          {m === "browse" ? "Browse" : "Design"}
        </button>
      ))}
    </div>
  );
}

export function AircraftDesignPanel({ projectId, persistedProject, currentModel, modeToggle, onOpenView }: {
  projectId: string;
  persistedProject: boolean;
  /** Browse mode's current catalog selection - the "from current model" source. */
  currentModel: AircraftCatalogModel | null;
  modeToggle: ReactNode;
  onOpenView: SurfaceProps["onOpenView"];
}) {
  // Designs list + selection.
  const [designs, setDesigns] = useState<AircraftDesignSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Active design state, all from the last ops response.
  const [bundle, setBundle] = useState<AircraftBuildResponse | null>(null);
  const [geoms, setGeoms] = useState<AircraftDesignGeom[]>([]);
  const [parmGeoms, setParmGeoms] = useState<AircraftGeomParms[]>([]);
  const [opsCount, setOpsCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolution, setResolution] = useState(12);

  // Viewer state - same name-keyed semantics as Browse.
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<string | null>(null);

  // Inline new-design form + design-level actions.
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newFrom, setNewFrom] = useState<"blank" | "model">("blank");
  const [creating, setCreating] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const copyableModel = currentModel && (currentModel.designable ?? currentModel.kind === "fleet")
    ? currentModel
    : null;

  // Pending (sent-but-unconfirmed or not-yet-flushed) parm edits - the
  // optimistic slider values layered over the last confirmed parm tree.
  const [pending, setPending] = useState<Record<string, number>>({});
  const pendingRef = useRef<Record<string, number>>({});
  const flushTimer = useRef<number | null>(null);

  // Ops serialization + stale-response guard (the buildSeq pattern): requests
  // queue on a promise chain because each one rewrites the design's .vsp3, and
  // only the newest request may apply its response.
  const opsSeq = useRef(0);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const opsArgs = useRef({ designId: activeId, res: resolution });
  opsArgs.current = { designId: activeId, res: resolution };
  const prevDesignRef = useRef<string | null>(null);

  const clearFlush = () => {
    if (flushTimer.current !== null) {
      window.clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
  };
  useEffect(() => clearFlush, []);

  /** Drop everything belonging to the previously active design. */
  const clearActive = useCallback(() => {
    setBundle(null);
    setGeoms([]);
    setParmGeoms([]);
    setOpsCount(null);
    setVisible({});
    setSelected(null);
    pendingRef.current = {};
    setPending({});
    clearFlush();
  }, []);

  const refreshDesigns = useCallback(() => {
    if (!persistedProject) return;
    listAircraftDesigns(projectId)
      .then((res) => {
        setDesigns(res.designs ?? []);
        setListError(null);
      })
      .catch((e) => {
        setDesigns((d) => d ?? []);
        setListError(errorMessage(e));
      });
  }, [persistedProject, projectId]);

  useEffect(() => {
    refreshDesigns();
  }, [refreshDesigns]);

  useEffect(() => {
    if (!copyableModel && newFrom === "model") setNewFrom("blank");
  }, [copyableModel, newFrom]);

  const applyResult = useCallback((res: AircraftDesignOpsResponse) => {
    setBundle(res.bundle);
    setGeoms(res.geoms ?? []);
    setParmGeoms((res.parms?.geoms ?? []).filter((g) => g.parms.length > 0));
    setOpsCount(res.ops_count);
    // Preserve visibility + selection across edits (names persist); new names
    // default to visible - same rule as Browse rebuilds.
    setVisible((v) => Object.fromEntries(res.bundle.components.map((c) => [c.name, v[c.name] !== false])));
    setSelected((s) => (s && res.bundle.components.some((c) => c.name === s) ? s : null));
  }, []);

  const runOps = useCallback(
    (designId: string, ops: AircraftDesignOp[], parmBatch?: Record<string, number>) => {
      const seq = ++opsSeq.current;
      setBusy(true);
      setError(null);
      chainRef.current = chainRef.current.then(async () => {
        try {
          const res = await applyAircraftDesignOps(projectId, designId, { ops, resolution: opsArgs.current.res });
          if (seq !== opsSeq.current || opsArgs.current.designId !== designId) return;
          applyResult(res);
          if (parmBatch) {
            // Confirmed edits leave the pending overlay - unless re-edited mid-flight.
            const still = pendingRef.current;
            for (const [id, v] of Object.entries(parmBatch)) {
              if (still[id] === v) delete still[id];
            }
            setPending({ ...still });
          }
        } catch (e) {
          if (seq !== opsSeq.current || opsArgs.current.designId !== designId) return;
          const msg = errorMessage(e);
          if (isGone(msg)) {
            // The design vanished under us (deleted elsewhere) - back to the list.
            setActiveId(null);
            clearActive();
            setError("That design no longer exists - it may have been deleted elsewhere.");
            refreshDesigns();
          } else {
            setError(msg);
          }
        } finally {
          if (seq === opsSeq.current) setBusy(false);
        }
      });
    },
    [applyResult, clearActive, projectId, refreshDesigns],
  );

  // Opening a design = the contract's empty ops batch (rebuild + describe).
  // Resolution changes reload the same way, flushing any unsent parm edits
  // along so they are never dropped.
  useEffect(() => {
    setConfirmingDelete(false);
    if (!persistedProject || !activeId) {
      prevDesignRef.current = activeId;
      return;
    }
    const switched = prevDesignRef.current !== activeId;
    prevDesignRef.current = activeId;
    if (switched) {
      clearFlush();
      pendingRef.current = {};
      setPending({});
      setGeoms([]);
      setParmGeoms([]);
      setOpsCount(null);
      setVisible({});
      setSelected(null);
      runOps(activeId, []);
    } else {
      const batch = { ...pendingRef.current };
      const hasBatch = Object.keys(batch).length > 0;
      clearFlush();
      runOps(activeId, hasBatch ? [{ op: "set_parms", values: batch }] : [], hasBatch ? batch : undefined);
    }
  }, [activeId, persistedProject, resolution, runOps]);

  /** Debounced (500ms) flush of accumulated parm edits as ONE set_parms op.
   *  The design the timer was armed for is captured at arm time - if the user
   *  switches designs in the narrow window between render and the switch
   *  effect's clearFlush, the stale timer must NOT send design A's pending
   *  parm ids at design B (they'd land as a miss burst in B's ops_log). */
  const scheduleFlush = useCallback(() => {
    clearFlush();
    const armedFor = opsArgs.current.designId;
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      const designId = opsArgs.current.designId;
      const batch = { ...pendingRef.current };
      if (!designId || designId !== armedFor || Object.keys(batch).length === 0) return;
      runOps(designId, [{ op: "set_parms", values: batch }], batch);
    }, 500);
  }, [runOps]);

  const setParm = useCallback(
    (parmId: string, value: number) => {
      pendingRef.current = { ...pendingRef.current, [parmId]: value };
      setPending(pendingRef.current);
      scheduleFlush();
    },
    [scheduleFlush],
  );

  // Resets discard UNSENT edits only - applied ops are history (provenance),
  // not something a reset can unsend.
  const resetGeom = useCallback((geom: AircraftGeomParms) => {
    const ids = new Set(geom.parms.map((p) => p.id));
    const kept = Object.fromEntries(Object.entries(pendingRef.current).filter(([id]) => !ids.has(id)));
    if (Object.keys(kept).length === Object.keys(pendingRef.current).length) return;
    pendingRef.current = kept;
    setPending(kept);
  }, []);

  const resetAll = useCallback(() => {
    if (Object.keys(pendingRef.current).length === 0) return;
    pendingRef.current = {};
    setPending({});
    clearFlush();
  }, []);

  // Component-tree ops.
  const addGeom = useCallback((type: AircraftDesignGeomType, parent: string | null) => {
    if (activeId) runOps(activeId, [{ op: "add_geom", type, parent, name: null }]);
  }, [activeId, runOps]);
  const renameGeom = useCallback((id: string, name: string) => {
    if (activeId) runOps(activeId, [{ op: "rename_geom", id, name }]);
  }, [activeId, runOps]);
  const deleteGeom = useCallback((id: string) => {
    if (activeId) runOps(activeId, [{ op: "delete_geom", id }]);
  }, [activeId, runOps]);

  const create = useCallback(async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setError(null);
    try {
      const from = newFrom === "model" && copyableModel ? { model_key: copyableModel.key } : undefined;
      const res = await createAircraftDesign(projectId, { name, from });
      setDesigns((d) => [...(d ?? []), { ...res, ops_count: 0 }]);
      setNewOpen(false);
      setNewName("");
      setActiveId(res.design_id);
      refreshDesigns();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setCreating(false);
    }
  }, [copyableModel, creating, newFrom, newName, projectId, refreshDesigns]);

  const active = designs?.find((d) => d.design_id === activeId) ?? null;

  const duplicate = useCallback(async () => {
    if (!active || creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await createAircraftDesign(projectId, {
        name: `${active.name} copy`,
        from: { design_id: active.design_id },
      });
      setDesigns((d) => [...(d ?? []), { ...res, ops_count: 0 }]);
      setActiveId(res.design_id);
      refreshDesigns();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setCreating(false);
    }
  }, [active, creating, projectId, refreshDesigns]);

  const removeDesign = useCallback(async () => {
    if (!activeId) return;
    setConfirmingDelete(false);
    setError(null);
    try {
      await deleteAircraftDesign(projectId, activeId);
    } catch (e) {
      const msg = errorMessage(e);
      if (!isGone(msg)) {
        setError(msg);
        return;
      }
      // Already gone elsewhere - fall through to the same cleanup.
    }
    opsSeq.current++; // orphan any in-flight ops for the deleted design
    setDesigns((d) => (d ?? []).filter((x) => x.design_id !== activeId));
    setActiveId(null);
    clearActive();
    setBusy(false);
    refreshDesigns();
  }, [activeId, clearActive, projectId, refreshDesigns]);

  // Bridge to the Control Surfaces workbench: export THIS design to STEP, seed
  // the CS surface with the real exported path, then navigate. A failed export
  // stays put and shows the API detail - no label-only fallback here, the
  // design exists nowhere else.
  const designControlSurfaces = useCallback(async () => {
    if (!activeId || !active || exporting) return;
    setExporting(true);
    setError(null);
    try {
      // Unsent (still-debouncing) parm edits must land in the .vsp3 BEFORE the
      // export reads it - flush them now and drain the ops chain, so the STEP
      // matches the panel and never races an in-flight .vsp3 rewrite.
      const batch = { ...pendingRef.current };
      if (Object.keys(batch).length > 0) {
        clearFlush();
        runOps(activeId, [{ op: "set_parms", values: batch }], batch);
      }
      await chainRef.current;
      if (opsArgs.current.designId !== activeId) return; // switched/deleted mid-flush
      const res = await exportAircraftDesignStep(projectId, activeId);
      publishSourceSeed({ stepPath: res.step_path, label: active.name });
      onOpenView("control-surfaces");
    } catch (e) {
      const msg = errorMessage(e);
      if (isGone(msg)) {
        setActiveId(null);
        clearActive();
        setError("That design no longer exists - it may have been deleted elsewhere.");
        refreshDesigns();
      } else {
        setError(msg);
      }
    } finally {
      setExporting(false);
    }
  }, [active, activeId, clearActive, exporting, onOpenView, projectId, refreshDesigns, runOps]);

  const toggle = (name: string) => setVisible((v) => ({ ...v, [name]: v[name] === false }));

  const stats = useMemo(() => {
    if (!bundle) return null;
    return {
      patches: bundle.total_patches.toLocaleString(),
      triangles: bundle.total_triangles.toLocaleString(),
      ms: Math.round(bundle.build_ms),
    };
  }, [bundle]);

  const edits = opsCount ?? active?.ops_count ?? null;

  // Design mode edits a project-owned file - without a saved project there is
  // nowhere to put it, so show a quiet note and fire no requests.
  if (!persistedProject) {
    return (
      <div style={S.root} data-testid="v5-aircraft-design">
        <div style={S.toolbar}>
          <span style={S.title}>Aircraft</span>
          {modeToggle}
        </div>
        <div style={S.emptyState}>
          <h2 style={{ margin: 0, fontSize: 15 }}>No saved project selected</h2>
          <p style={{ margin: 0, maxWidth: 420, lineHeight: 1.5 }}>
            Aircraft designs live on a saved Thread project - open one from Projects, then
            come back to design here. Browse mode works without a project.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.root} data-testid="v5-aircraft-design">
      <div style={S.toolbar}>
        <span style={S.title}>Aircraft</span>
        {modeToggle}
        <select style={S.select} value={activeId ?? ""} onChange={(e) => setActiveId(e.target.value || null)}
          disabled={creating || designs === null} aria-label="Design">
          <option value="">
            {designs === null ? "Loading designs…" : designs.length === 0 ? "No designs yet" : "Select a design…"}
          </option>
          {(designs ?? []).map((d) => <option key={d.design_id} value={d.design_id}>{d.name}</option>)}
        </select>
        {edits !== null && active && (
          <span style={S.prov} title="Every applied ops batch is appended to this design's provenance log">
            {edits} edit{edits === 1 ? "" : "s"} recorded
          </span>
        )}
        <button style={S.buttonGhost} onClick={() => setNewOpen((o) => !o)} disabled={creating}>
          New aircraft
        </button>
        {active && (
          <button style={S.buttonGhost} onClick={() => { void duplicate(); }} disabled={creating || busy}>
            {creating ? "Copying…" : "Duplicate"}
          </button>
        )}
        {active && !confirmingDelete && (
          <button style={S.buttonGhost} onClick={() => setConfirmingDelete(true)} disabled={creating}>
            Delete
          </button>
        )}
        {active && confirmingDelete && (
          <span style={S.confirmWrap}>
            <button style={S.dangerBtn} onClick={() => { void removeDesign(); }}>Delete "{active.name}"</button>
            <button style={S.buttonGhost} onClick={() => setConfirmingDelete(false)}>Keep</button>
          </span>
        )}
        {active && (
          <label style={S.field}>
            res
            <select style={S.select} value={resolution} onChange={(e) => setResolution(Number(e.target.value))}>
              {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        )}
        {active && (
          <button style={S.buttonGhost} onClick={() => { void designControlSurfaces(); }} disabled={exporting || busy}>
            {exporting ? "Exporting…" : "Design control surfaces"}
          </button>
        )}
        {stats && (
          <span style={S.stat}>
            {stats.patches} Bézier patches · {stats.triangles} tris · {stats.ms} ms
          </span>
        )}
      </div>

      {newOpen && (
        <div style={S.newRow}>
          <input style={S.input} placeholder="Design name" value={newName} autoFocus
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void create();
              if (e.key === "Escape") setNewOpen(false);
            }}
            aria-label="New design name" />
          <select style={S.select} value={newFrom} onChange={(e) => setNewFrom(e.target.value as "blank" | "model")}
            aria-label="New design source">
            <option value="blank">Start blank</option>
            {copyableModel && <option value="model">Copy {copyableModel.label}</option>}
          </select>
          <button style={S.button} onClick={() => { void create(); }} disabled={creating || !newName.trim()}>
            {creating ? "Creating…" : "Create"}
          </button>
          <button style={S.buttonGhost} onClick={() => setNewOpen(false)} disabled={creating}>Cancel</button>
        </div>
      )}

      <div style={S.body}>
        <div style={S.viewer}>
          {!activeId ? (
            <div style={S.empty}>
              {designs === null
                ? "Loading designs…"
                : designs.length === 0
                  ? "No designs yet - create one with New aircraft. Start blank, or copy the model selected in Browse."
                  : "Pick a design to open it, or create a new one."}
            </div>
          ) : error && !bundle ? (
            <div style={S.error}>{error}</div>
          ) : (
            <>
              <AircraftViewer3D modelUrl={bundle?.model_url ?? null} visible={visible}
                selected={selected} onSelect={setSelected} />
              {/* Edits keep the last good model on screen - slim overlays only. */}
              {busy && <div style={S.busy}>{bundle ? "Applying…" : "Opening design…"}</div>}
              {error && bundle && <div style={S.errorBanner}>{error}</div>}
            </>
          )}
          {!activeId && (error || listError) && <div style={S.errorBanner}>{error ?? listError}</div>}
        </div>

        {activeId && (
          <div style={S.panel}>
            <AircraftComponentTree
              geoms={geoms}
              components={bundle?.components ?? []}
              visible={visible}
              selected={selected}
              busy={busy}
              onToggle={toggle}
              onSelect={setSelected}
              onAdd={addGeom}
              onRename={renameGeom}
              onDelete={deleteGeom}
            />
            {parmGeoms.length > 0 && (
              <AircraftParamPanel
                geoms={parmGeoms}
                overrides={pending}
                disabled={busy}
                onChange={setParm}
                onResetGeom={resetGeom}
                onResetAll={resetAll}
              />
            )}
            <div style={S.note}>
              Every edit is applied through OpenVSP to this design's .vsp3 and appended to
              its ops log - the log is the design's provenance record.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  root: { display: "flex", flexDirection: "column", height: "100%", width: "100%",
    background: "var(--v5-surface)", color: "var(--v5-ink)", font: "13px var(--v5-sans, system-ui)" },
  toolbar: { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
    borderBottom: "1px solid var(--v5-line)", flexWrap: "wrap" },
  title: { fontWeight: 600, fontSize: 14, letterSpacing: 0.2 },
  modeWrap: { display: "flex", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", overflow: "hidden" },
  modeTab: { background: "var(--v5-surface)", color: "var(--v5-ink)", border: "none",
    padding: "3px 10px", cursor: "pointer", font: "inherit", opacity: 0.7 },
  modeTabOn: { background: "var(--v5-primary)", color: "#fff", opacity: 1 },
  field: { display: "flex", alignItems: "center", gap: 4, color: "var(--v5-ink)", opacity: 0.8 },
  select: { background: "var(--v5-surface)", color: "var(--v5-ink)", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", padding: "3px 6px", font: "inherit" },
  input: { background: "var(--v5-surface)", color: "var(--v5-ink)", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", padding: "3px 8px", font: "inherit", minWidth: 180 },
  button: { background: "var(--v5-primary)", color: "#fff", border: "none",
    borderRadius: "var(--v5-radius, 4px)", padding: "4px 12px", cursor: "pointer", font: "inherit" },
  buttonGhost: { background: "var(--v5-surface)", color: "var(--v5-ink)",
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "4px 12px", cursor: "pointer", font: "inherit" },
  dangerBtn: { background: "var(--v5-surface)", color: "var(--v5-amber, #c80)",
    border: "1px solid var(--v5-amber, #c80)", borderRadius: "var(--v5-radius, 4px)",
    padding: "4px 12px", cursor: "pointer", font: "inherit" },
  confirmWrap: { display: "flex", alignItems: "center", gap: 6 },
  prov: { font: "11px var(--v5-mono, monospace)", opacity: 0.65, whiteSpace: "nowrap" },
  stat: { marginLeft: "auto", font: "12px var(--v5-mono, monospace)", opacity: 0.75 },
  newRow: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
    borderBottom: "1px solid var(--v5-line)", flexWrap: "wrap" },
  body: { display: "flex", flex: 1, minHeight: 0 },
  viewer: { flex: 1, minWidth: 0, position: "relative" },
  panel: { width: 260, borderLeft: "1px solid var(--v5-line)", display: "flex",
    flexDirection: "column", overflowY: "auto" },
  note: { padding: 12, fontSize: 11, opacity: 0.6, lineHeight: 1.5, marginTop: "auto" },
  empty: { position: "absolute", inset: 0, display: "grid", placeItems: "center",
    padding: 24, textAlign: "center", opacity: 0.65, lineHeight: 1.6 },
  emptyState: { flex: 1, display: "flex", flexDirection: "column", gap: 8,
    alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", opacity: 0.8 },
  error: { position: "absolute", inset: 0, display: "grid", placeItems: "center",
    padding: 24, textAlign: "center", color: "var(--v5-amber, #c80)", font: "13px var(--v5-mono, monospace)" },
  busy: { position: "absolute", top: 8, left: 8, padding: "2px 10px",
    background: "var(--v5-surface)", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", font: "11px var(--v5-mono, monospace)", opacity: 0.85 },
  errorBanner: { position: "absolute", bottom: 8, left: 8, right: 8, padding: "4px 10px",
    background: "var(--v5-surface)", border: "1px solid var(--v5-line)",
    borderRadius: "var(--v5-radius, 4px)", color: "var(--v5-amber, #c80)",
    font: "12px var(--v5-mono, monospace)" },
};
